/* 인디포토 사진(현장별) — 사진 고르기 · 편집기 사진 탭이 같이 쓴다 */
import { showcaseApi, errorMessage } from '../api/showcaseApi';
import { photoSrc } from '../model/images';
import type { ShowcasePhoto, ShowcaseSite } from '../model/types';
import { useAuthStore } from '@indipia/shared/stores/authStore';
import { ed } from './editor';

/** mine: "내 현장만 보기" — 켜면 내가 담당자·관리자인 현장만, 끄면(기본) 우리 회사 모든 현장. 브라우저에 기억한다 */
const MINE_KEY = 'showcase.mineOnly';
const readMine = () => { try { return localStorage.getItem(MINE_KEY) === 'Y'; } catch { return false; } };
export const photoState: { sites: ShowcaseSite[] | null; loading: boolean; error: string | null; mine: boolean } =
  { sites: null, loading: false, error: null, mine: readMine() };
const cache: Partial<Record<'all' | 'mine', ShowcaseSite[]>> = {};
let inflight: Promise<ShowcaseSite[]> | null = null;

/* 받아 둔 목록은 로그인한 회원·회사 것 — 다른 계정으로 다시 로그인하면(화면 새로고침 없이) 버린다 */
const ownerOf = () => { const u = useAuthStore.getState().user; return u ? `${u.memberId}:${u.compId}` : ''; };
let owner = ownerOf();
function checkOwner() {
  const now = ownerOf();
  if (now === owner) return;
  owner = now;
  delete cache.all; delete cache.mine;
  inflight = null;
  photoState.sites = null;
  photoState.error = null;
  photoState.loading = false;
}
useAuthStore.subscribe(() => { if (ownerOf() !== owner) { checkOwner(); ed.bump(); } });

export function loadSites(force = false): Promise<ShowcaseSite[]> {
  checkOwner();
  const key = photoState.mine ? 'mine' : 'all';
  if (cache[key] && !force) { photoState.sites = cache[key]!; return Promise.resolve(photoState.sites); }
  if (inflight) return inflight;
  photoState.loading = true;
  photoState.error = null;
  ed.bump();
  const who = owner;
  const req: Promise<ShowcaseSite[]> = showcaseApi.photos(null, key === 'mine')
    .then((sites) => {
      if (who !== owner) return photoState.sites || []; // 받는 사이 계정이 바뀌었으면 버린다
      cache[key] = sites.filter((s) => s.photos.length);
      if ((photoState.mine ? 'mine' : 'all') === key) photoState.sites = cache[key]!;
      return cache[key]!;
    })
    .catch((e) => {
      if (who !== owner) return photoState.sites || [];
      photoState.error = errorMessage(e);
      photoState.sites = photoState.sites || [];
      return photoState.sites;
    })
    .finally(() => {
      if (inflight === req) { photoState.loading = false; inflight = null; }
      ed.bump();
    });
  inflight = req;
  return req;
}

/** "내 현장만 보기" 켜고 끄기 — 바꾼 쪽 목록을 불러온다 */
export async function setMineOnly(on: boolean) {
  photoState.mine = on;
  try { localStorage.setItem(MINE_KEY, on ? 'Y' : 'N'); } catch { /* 저장 못 해도 이번 화면에서는 적용 */ }
  photoState.sites = cache[on ? 'mine' : 'all'] || null;
  ed.bump();
  if (inflight) await inflight;
  return loadSites();
}

/** 사진이 하나도 없었으면 다시 불러온다 — 인디포토에서 사진을 올리고 돌아왔을 때 */
export function refreshSitesIfEmpty() {
  return loadSites(!!photoState.sites && !photoState.sites.length);
}
// 다른 탭에서 사진을 올리고 이 탭으로 돌아왔을 때도
if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => { if (photoState.sites && !photoState.sites.length && !photoState.loading) void loadSites(true); });
}

/** 사진의 현장 — 받아 둔 목록(회사 전체·내 현장)에서 찾는다. 모르면 undefined */
export function photoSiteOf(photoId: number | undefined): number | undefined {
  if (!photoId) return undefined;
  for (const list of [cache.all, cache.mine]) for (const s of list || []) if (s.photos.some((p) => p.photoId === photoId)) return s.siteId;
  return undefined;
}

/** 사진 공간 — 공간을 모르면 기타 */
export const spaceOf = (p: ShowcasePhoto) => p.spaceTag || 'ETC';
export const srcOf = (p: ShowcasePhoto) => (p.externalId ? photoSrc(p.externalId) : '');

/** 공간을 바꾼 사진을 목록에도 반영 */
export function patchPhotoSpace(photoId: number, spaceTag: string, spaceName: string, source: ShowcasePhoto['spaceSource']) {
  // 회사 전체·내 현장 목록 둘 다(같은 사진이 두 목록에 따로 있다)
  [cache.all, cache.mine].forEach((list) => list?.forEach((s) => s.photos.forEach((p) => {
    if (p.photoId === photoId) { p.spaceTag = spaceTag; p.spaceName = spaceName; p.spaceSource = source; }
  })));
  ed.bump();
}
